import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, buildQuery } from '../lib/api';
import { primaryInstance } from '../lib/utils';
import { useToast } from '../components/Feedback';
import type {
    Application,
    CatalogFilters,
    CatalogResponse,
    Category,
    HomeResponse,
} from '../types';

export function useCategories() {
    return useQuery({
        queryKey: ['categories'],
        queryFn: () => api.get<Category[]>('/api/catalog/categories'),
        staleTime: 5 * 60_000,
    });
}

export function useCatalog(filters: CatalogFilters) {
    const query = buildQuery({
        search: filters.search,
        category: filters.category,
        environment: filters.environment,
        network: filters.network,
        favorites: filters.favoritesOnly,
    });

    return useQuery({
        queryKey: ['catalog', filters],
        queryFn: () => api.get<CatalogResponse>(`/api/catalog${query}`),
        placeholderData: (previous) => previous,
    });
}

export function useHome() {
    return useQuery({
        queryKey: ['catalog-home'],
        queryFn: () => api.get<HomeResponse>('/api/catalog/home'),
    });
}

export function useApplication(idOrSlug: string | undefined) {
    return useQuery({
        queryKey: ['application', idOrSlug],
        queryFn: () => api.get<Application>(`/api/catalog/${idOrSlug}`),
        enabled: Boolean(idOrSlug),
    });
}

export function useToggleFavorite() {
    const queryClient = useQueryClient();
    const { notify } = useToast();

    return useMutation({
        mutationFn: (application: Application) =>
            api.post<{ isFavorite: boolean }>(`/api/catalog/${application.id}/favorite`),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['catalog'] });
            void queryClient.invalidateQueries({ queryKey: ['catalog-home'] });
            void queryClient.invalidateQueries({ queryKey: ['application'] });
        },
        onError: () => notify('Le favori n’a pas pu être enregistré.', 'error'),
    });
}

/**
 * Glisser-déposer dans « Vos favoris ».
 *
 * Le panneau est mis à jour avant la réponse du serveur : un glisser-déposer
 * qui attend un aller-retour réseau avant de bouger se sent cassé. L'ordre
 * précédent est restauré si l'appel échoue.
 */
export function useReorderFavorite() {
    const queryClient = useQueryClient();
    const { notify } = useToast();

    return useMutation({
        mutationFn: ({
            applicationId,
            beforeApplicationId,
        }: {
            applicationId: string;
            beforeApplicationId: string | null;
        }) =>
            api.post('/api/catalog/favorites/reorder', { applicationId, beforeApplicationId }),
        onMutate: async ({ applicationId, beforeApplicationId }) => {
            await queryClient.cancelQueries({ queryKey: ['catalog-home'] });
            const previous = queryClient.getQueryData<HomeResponse>(['catalog-home']);

            if (previous) {
                const favorites = [...previous.favorites];
                const from = favorites.findIndex((a) => a.id === applicationId);

                if (from !== -1) {
                    const [moved] = favorites.splice(from, 1);
                    const to = beforeApplicationId
                        ? favorites.findIndex((a) => a.id === beforeApplicationId)
                        : -1;
                    favorites.splice(to === -1 ? favorites.length : to, 0, moved);

                    queryClient.setQueryData<HomeResponse>(['catalog-home'], {
                        ...previous,
                        favorites,
                    });
                }
            }

            return { previous };
        },
        onError: (_error, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(['catalog-home'], context.previous);
            }
            notify('L’ordre des favoris n’a pas pu être enregistré.', 'error');
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['catalog-home'] });
        },
    });
}

/**
 * Ouvre une application dans un nouvel onglet.
 *
 * La fenêtre est ouverte de façon synchrone, avant l'appel réseau qui trace la
 * visite : un `window.open` déclenché après un `await` est bloqué par les
 * navigateurs, qui ne le rattachent plus au clic de l'utilisateur.
 */
export function useOpenApplication() {
    const { notify } = useToast();

    return useCallback(
        (application: Application, instanceId?: string) => {
            const instance = instanceId
                ? (application.instances.find((i) => i.id === instanceId) ??
                  primaryInstance(application))
                : primaryInstance(application);

            if (!instance) {
                notify('Cette application ne déclare aucune adresse à ouvrir.', 'info');
                return;
            }

            window.open(instance.url, '_blank', 'noopener,noreferrer');

            /* La trace d'usage n'est demandée que sur un droit enregistré.
               L'API refuse par construction de tracer l'ouverture d'une
               application fermée, pour ne pas la faire remonter parmi les
               récentes aux côtés de celles auxquelles on a réellement accès.
               Envoyer l'appel quand même ne produirait qu'un 403 inutile. */
            if (!application.hasAccess) {
                return;
            }

            void api
                .post(`/api/catalog/${application.id}/open`, { instanceId: instance.id })
                .catch(() => {
                    // La trace d'usage est un confort : son échec ne doit pas
                    // remonter à l'utilisateur, qui a déjà son application ouverte.
                });
        },
        [notify]
    );
}
